import { inject, Injectable, Provider } from "@angular/core";
import { Observable, Observer, Subscription } from "rxjs";
import {
  EImageSize, EProductAssetType, IProductAvailabilityGetQuery, IProductAvailabilityGetQueryResult, ProductAssetGetByIdQuery, ProductAssetGetByIdQueryResult,
  ProductAssetGetQuery, ProductAssetGetQueryResult, ProductAssortmentGetQuery, ProductAssortmentGetQueryResult,
  ProductAvailabilityGetQuery, ProductClient,
  ProductDetailGetQuery, ProductDetailGetQueryResult, ProductGetByIntegraQueryQuery,
  ProductGetByIntegraQueryQueryResult, ProductGetByProductCategoryQuery, ProductGetByProductCategoryQueryResult,
  ProductGetByTechGroupFilterQuery, ProductGetByTechGroupFilterQueryResult, ProductGetCategoryTreeQuery,
  ProductGetCategoryTreeQueryResult, ProductIdentifierDto, ProductIdentifierGetQuery, ProductIdentifierGetQueryResult,
  ProductOptionalGetQuery, ProductOptionalGetQueryResult, ProductOrderPriceGetItemDto, ProductOrderPriceGetQuery,
  ProductOrderPriceGetQueryResult, ProductPackagingTypeGetQuery, ProductPackagingTypeGetQueryResult, ProductPriceInfoGetItemDto,
  ProductPriceInfoGetQuery, ProductPriceInfoGetQueryResult, ProductPromotionDetailGetQuery,
  ProductPromotionGetQuery, ProductPromotionGetQueryResult, ProductQuantityScaleGetQuery, ProductQuantityScaleGetQueryResult,
  ProductSearchQuery, ProductSearchQueryResult, ProductStockInformationGetDto, ProductStockInformationGetQuery,
  ProductStockInformationGetQueryResult, ProductThumbnailGetQuery, ProductThumbnailGetQueryResult, TechGroupFilterDto
} from "../../INTEGRA_Shop_Client";
import { IProductApiService, IProductApiServiceToken } from "./product-api-service.interface";
import { IComparisonService, IComparisonServiceToken } from "../../infrastructure/comparison/comparison-service.interface";

@Injectable({
  providedIn: 'root'
})
export class ProductApiService implements IProductApiService {
  private readonly productClient = inject(ProductClient);
  private readonly comparisonService: IComparisonService = inject(IComparisonServiceToken);

  private productDetailGetSubscription: Subscription | undefined;
  private productPriceInfoGetSubscription: Subscription | undefined;
  private productThumbnailGetSubscription: Subscription | undefined;
  private productStockInformationGetSubscription: Subscription | undefined;
  private productAvailabilityGetSubscription: Subscription | undefined;
  private productSearchSubscription: Subscription | undefined;

  private lastProductThumbnailGetQuery: ProductThumbnailGetQuery | undefined;
  private lastProductDetailGetQuery: ProductDetailGetQuery | undefined;
  private lastProductPriceInfoGetQuery: ProductPriceInfoGetQuery | undefined;
  private lastProductStockInformationGetQuery: ProductStockInformationGetQuery | undefined;

  public productIdentifierGet(
    productList?: ProductIdentifierDto[] | undefined,
  ): Observable<ProductIdentifierGetQueryResult> {
    let query = new ProductIdentifierGetQuery({
      productList: productList
    });
    return this.productClient.productIdentifierGet(query);
  }

  public productSearch(
    searchText?: string | undefined,
    maxResultCount?: number | undefined,
    useCancellationLogic: boolean | undefined = false
  ): Observable<ProductSearchQueryResult> {
    let query = new ProductSearchQuery({
      searchText: searchText,
      maxResultCount: maxResultCount
    });

    if (useCancellationLogic && this.productSearchSubscription != undefined
    ) {
      this.productSearchSubscription.unsubscribe();
    }

    return new Observable<ProductSearchQueryResult>((observer: Observer<ProductSearchQueryResult>) => {
      observer.complete = () => {
        this.productSearchSubscription = undefined;
      };

      this.productSearchSubscription = this.productClient.productSearch(query).subscribe({
        next(result) {
          observer.next(result);
          observer.complete();
        },
        error(error) {
          observer.error(error);
        },
        complete() {
          observer.complete();
        },
      });
    });
  }

  public productDetailGet(
    loadDetailText: boolean,
    loadToolTipText: boolean,
    loadProductComparsionText: boolean,
    loadMetaDescriptionText: boolean,
    loadTagList: boolean,
    checkAvailabilityNotification: boolean,
    checkIsPromotionProduct: boolean,
    requestDate?: Date | undefined,
    productList?: ProductIdentifierDto[] | undefined,
    useCancellationLogic: boolean | undefined = true
  ): Observable<ProductDetailGetQueryResult> {
    let query = new ProductDetailGetQuery({
      productList: productList,
      loadDetailText: loadDetailText,
      loadToolTipText: loadToolTipText,
      loadProductComparisonText: loadProductComparsionText,
      loadMetaDescriptionText: loadMetaDescriptionText,
      loadTagList: loadTagList,
      checkAvailabilityNotification: checkAvailabilityNotification,
      checkIsPromotionProduct: checkIsPromotionProduct,
      requestDate: requestDate,
    });

    if (useCancellationLogic
      && this.productDetailGetSubscription != undefined
      && this.comparisonService.equalsGenericDeepSlow(this.lastProductDetailGetQuery, query)
    ) {
      this.productDetailGetSubscription.unsubscribe();
    }

    this.lastProductDetailGetQuery = query;

    return new Observable<ProductDetailGetQueryResult>((observer: Observer<ProductDetailGetQueryResult>) => {
      this.productDetailGetSubscription = this.productClient.productDetailGet(query).subscribe(
        result => {
          observer.next(result);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }

  public productOrderPriceGet(
    productList?: ProductOrderPriceGetItemDto[] | undefined,
    deliveryAddressNumber?: number | undefined,
  ): Observable<ProductOrderPriceGetQueryResult> {
    let query = new ProductOrderPriceGetQuery({
      productList: productList,
      deliveryAddressNumber: deliveryAddressNumber,
    });
    return this.productClient.productOrderPriceGet(query);
  }

  public productPriceInfoGet(
    productList?: ProductPriceInfoGetItemDto[] | undefined,
    deliveryAddressNumber?: number | undefined,
    useCancellationLogic: boolean | undefined = true
  ): Observable<ProductPriceInfoGetQueryResult> {
    let query = new ProductPriceInfoGetQuery({
      productList: productList,
      deliveryAddressNumber: deliveryAddressNumber,
    });

    if (useCancellationLogic
      && this.productPriceInfoGetSubscription != undefined
      && this.comparisonService.equalsGenericDeepSlow(this.lastProductPriceInfoGetQuery, query)
    ) {
      this.productPriceInfoGetSubscription.unsubscribe();
    }

    this.lastProductPriceInfoGetQuery = query;

    return new Observable((observer: Observer<ProductPriceInfoGetQueryResult>) => {
      this.productPriceInfoGetSubscription = this.productClient.productPriceInfoGet(query).subscribe(
        result => {
          observer.next(result);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }

  public productAvailabilityGet(
    params?: Partial<IProductAvailabilityGetQuery>,
    allowCancellation: boolean = true,
  ): Observable<IProductAvailabilityGetQueryResult> {
    let query = new ProductAvailabilityGetQuery({
      productList: params?.productList,
      requestDate: params?.requestDate
    });

    // no cancellation: normal request
    if (!allowCancellation) {
      return this.productClient.productAvailabilityGet(query);
    }

    // cancellation: kill old subscription
    if (this.productAvailabilityGetSubscription) {
      this.productAvailabilityGetSubscription.unsubscribe();
      this.productAvailabilityGetSubscription = undefined;
    }

    // new observable with manuel subscription management
    return new Observable<IProductAvailabilityGetQueryResult>(observer => {
      this.productAvailabilityGetSubscription = this.productClient.productAvailabilityGet(query).subscribe({
        next: (result) => observer.next(result),
        error: (error) => {
          observer.error(error);
          this.productAvailabilityGetSubscription = undefined;
        },
        complete: () => {
          observer.complete();
          this.productAvailabilityGetSubscription = undefined;
        }
      });

      // cleanup upon component unsubscribed
      return () => {
        if (this.productAvailabilityGetSubscription) {
          this.productAvailabilityGetSubscription.unsubscribe();
          this.productAvailabilityGetSubscription = undefined;
        }
      };
    });
  }

  public productStockInformationGet(
    productList?: ProductStockInformationGetDto[] | undefined,
    requestDate?: Date | undefined,
    useCancellationLogic: boolean | undefined = true
  ): Observable<ProductStockInformationGetQueryResult> {
    let query = new ProductStockInformationGetQuery({
      productList: productList,
      requestDate: requestDate
    });

    if (useCancellationLogic
      && this.productStockInformationGetSubscription !== undefined
      && this.comparisonService.equalsGenericDeepSlow(this.lastProductStockInformationGetQuery, query)
    ) {
      this.productStockInformationGetSubscription.unsubscribe();
    }

    this.lastProductStockInformationGetQuery = query;

    return new Observable<ProductStockInformationGetQueryResult>((observer: Observer<ProductStockInformationGetQueryResult>) => {
      this.productStockInformationGetSubscription = this.productClient.productStockInformationGet(query).subscribe(
        result => {
          observer.next(result);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }

  public productThumbnailGet(
    imageSize: EImageSize,
    productList?: ProductIdentifierDto[] | undefined,
    useCancellationLogic: boolean | undefined = true
  ): Observable<ProductThumbnailGetQueryResult> {
    let query = new ProductThumbnailGetQuery({
      productList: productList,
      imageSize: imageSize,
    });

    if (useCancellationLogic
      && this.productThumbnailGetSubscription != undefined
      && this.comparisonService.equalsGenericDeepSlow(this.lastProductThumbnailGetQuery, query)
    ) {
      this.productThumbnailGetSubscription.unsubscribe();
    }

    this.lastProductThumbnailGetQuery = query;

    return new Observable<ProductThumbnailGetQueryResult>((observer: Observer<ProductThumbnailGetQueryResult>) => {
      this.productThumbnailGetSubscription = this.productClient.productThumbnailGet(query).subscribe(
        result => {
          observer.next(result);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }

  public productAssetGet(
    productList?: ProductIdentifierDto[] | undefined,
    productAssetType?: EProductAssetType | undefined,
  ): Observable<ProductAssetGetQueryResult> {
    let query = new ProductAssetGetQuery({
      productList: productList,
      productAssetType: productAssetType,
    });
    return this.productClient.productAssetGet(query);
  }

  public productAssetGetById(
    assetId: number,
  ): Observable<ProductAssetGetByIdQueryResult> {
    let query = new ProductAssetGetByIdQuery({
      assetId: assetId,
    });
    return this.productClient.productAssetGetById(query);
  }

  public productQuantityScaleGet(
    productList?: ProductIdentifierDto[] | undefined,
  ): Observable<ProductQuantityScaleGetQueryResult> {
    let query = new ProductQuantityScaleGetQuery({
      productList: productList,
    });
    return this.productClient.productQuantityScaleGet(query);
  }

  public productAssortmentGet(
    productList?: ProductIdentifierDto[] | undefined,
  ): Observable<ProductAssortmentGetQueryResult> {
    let query = new ProductAssortmentGetQuery({
      productList: productList,
    });
    return this.productClient.productAssortmentGet(query);
  }

  public productOptionalGet(
    productList?: ProductIdentifierDto[] | undefined,
  ): Observable<ProductOptionalGetQueryResult> {
    let query = new ProductOptionalGetQuery({
      productList: productList,
    });
    return this.productClient.productOptionalGet(query);
  }

  public productGetByTechGroupFilter(
    techGroupFilter?: TechGroupFilterDto | undefined,
  ): Observable<ProductGetByTechGroupFilterQueryResult> {
    let query = new ProductGetByTechGroupFilterQuery({
      techGroupFilter: techGroupFilter,
    });
    return this.productClient.productGetByTechGroupFilter(query);
  }

  public productGetByProductCategory(
    includeSubCategoryProducs: boolean,
    loadTechGroupNumberList: boolean,
    categoryKey?: string | undefined,
  ): Observable<ProductGetByProductCategoryQueryResult> {
    let query = new ProductGetByProductCategoryQuery({
      categoryKey: categoryKey,
      includeSubCategoryProducts: includeSubCategoryProducs,
      loadTechGroupNumberList: loadTechGroupNumberList
    });
    return this.productClient.productGetByProductCategory(query);
  }

  public productGetCategoryTree(
    productList?: ProductIdentifierDto[] | undefined,
  ): Observable<ProductGetCategoryTreeQueryResult> {
    let query = new ProductGetCategoryTreeQuery({
      productList: productList,
    });
    return this.productClient.productGetCategoryTree(query);
  }

  public productPromotionGet(requestDate?: Date): Observable<ProductPromotionGetQueryResult> {
    let query = new ProductPromotionGetQuery({ requestDate: requestDate });
    return this.productClient.productPromotionGet(query);
  }

  public productPromotionDetailGet(
    productIdentifierList: ProductIdentifierDto[],
    requestDate: Date | undefined
  ) {
    let query = new ProductPromotionDetailGetQuery(
      {
        productIdentifierList,
        requestDate
      }
    );
    return this.productClient.productPromotionDetailGet(query);
  }

  public productGetByIntegraQuery(
    queryId: number,
    queryParameters?: { [key: string]: string; } | undefined,
  ): Observable<ProductGetByIntegraQueryQueryResult> {
    let query = new ProductGetByIntegraQueryQuery({
      queryId: queryId,
      queryParameters: queryParameters,
    });
    return this.productClient.productGetByIntegraQuery(query);
  }

  public productPackagingTypeGet(
    productIdentifierList?: ProductIdentifierDto[] | undefined
  ): Observable<ProductPackagingTypeGetQueryResult> {
    let query = new ProductPackagingTypeGetQuery({
      productIdentifierList: productIdentifierList
    });
    return this.productClient.productPackagingTypeGet(query);
  }

}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const ProductApiServiceProvider: Provider[] = [
  { provide: ProductClient },
  { provide: IProductApiServiceToken, useExisting: ProductApiService }
];
